'use client';

import { useState } from 'react';

import {
  ChevronRight,
  Loader2,
  RefreshCcw,
  ShieldCheck,
  Star,
} from 'lucide-react';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { CheckoutOverlay } from '~/components/billing/checkout-overlay';
import { useDisplayCurrency } from '~/components/currency-provider';
import { UPGRADE_DISCOUNT_PERCENT } from '~/config/monetization.config';

const MONTHLY_PLAN_ID = 'pass-monthly';

/**
 * "UNLOCK MORE" premium tile injected into the character grids (home +
 * catalog). Design: the neon-trio purchase artwork as the card, offer badges
 * on top, pricing + CTA in the artwork's dark lower half. Clicking starts
 * the monthly checkout directly (auth modal for signed-out users).
 */
export function UpgradePromoCard() {
  const { symbol } = useDisplayCurrency();
  const { openAuthModal } = useAuthModal();
  const [loading, setLoading] = useState(false);
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: MONTHLY_PLAN_ID }),
      });

      // Only 401 means "sign in needed"; everything else we send to /pricing.
      if (res.status === 401) {
        openAuthModal('sign-up', '/pricing');
        return;
      }

      const data = (await res.json().catch(() => ({}))) as {
        checkoutToken?: string;
      };

      if (data.checkoutToken) {
        setCheckoutToken(data.checkoutToken);
      } else {
        window.location.href = '/pricing';
      }
    } catch {
      window.location.href = '/pricing';
    } finally {
      setLoading(false);
    }
  };

  // Per-element "pop" on hover. Compounds with the card's own scale below.
  const pop = (scale: number) => ({
    transition: 'transform 0.4s ease',
    transform: hovered ? `scale(${scale})` : 'scale(1)',
  });

  return (
    <>
      <button
        type="button"
        onClick={handleUpgrade}
        disabled={loading}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={`Unlock more — 3-day free trial, then ${symbol}5.99 per month`}
        className="relative block w-full overflow-hidden rounded-3xl text-center disabled:cursor-wait"
        style={{
          containerType: 'inline-size',
          aspectRatio: '324 / 520',
          background:
            "url('/images/backgrounds/purchase-popup.avif') center / cover no-repeat, linear-gradient(180deg,#180c30,#0b0417)",
          border: '1px solid rgba(236,72,153,0.55)',
          transition:
            'transform 0.4s ease, filter 0.4s ease, box-shadow 0.4s ease',
          transform: hovered ? 'scale(1.02)' : 'scale(1)',
          filter: hovered ? 'brightness(1.08)' : 'brightness(1)',
          boxShadow: hovered
            ? '0 0 34px rgba(236,72,153,0.7), 0 0 85px rgba(236,72,153,0.55), inset 0 0 30px rgba(236,72,153,0.16)'
            : '0 0 10px rgba(236,72,153,0.22), 0 0 40px rgba(236,72,153,0), inset 0 0 22px rgba(236,72,153,0.10)',
        }}
      >
        {/* Bottom scrim so text always reads over the art */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0"
          style={{
            height: '62%',
            background:
              'linear-gradient(to top, rgba(8,3,16,0.96) 0%, rgba(8,3,16,0.72) 55%, transparent 100%)',
          }}
        />

        {/* Top badges */}
        <span
          className="absolute inset-x-0 top-0 flex items-start justify-between"
          style={{ padding: '5cqw 5.5cqw 0' }}
        >
          <span
            className="inline-flex items-center font-bold text-white uppercase"
            style={{
              gap: '1.6cqw',
              padding: '2cqw 4cqw',
              fontSize: '4.2cqw',
              borderRadius: '999px',
              background: 'linear-gradient(90deg,#ec4899,#d946ef)',
              boxShadow: '0 0 16px rgba(236,72,153,0.6)',
              ...pop(1.05),
            }}
          >
            <Star
              style={{ width: '4.2cqw', height: '4.2cqw' }}
              fill="currentColor"
            />
            Limited Offer
          </span>
          <span
            className="flex flex-col items-center font-extrabold text-white"
            style={{
              padding: '2.4cqw 4cqw 3cqw',
              fontSize: '4cqw',
              lineHeight: 1.15,
              background: '#e11d63',
              borderRadius: '0 0 3cqw 3cqw',
              clipPath:
                'polygon(0 0, 100% 0, 100% 100%, 50% calc(100% - 2.4cqw), 0 100%)',
              boxShadow: '0 0 18px rgba(225,29,99,0.5)',
              ...pop(1.05),
            }}
          >
            SAVE
            <span style={{ fontSize: '6.4cqw' }}>
              {UPGRADE_DISCOUNT_PERCENT}%
            </span>
          </span>
        </span>

        {/* Content in the dark lower half */}
        <span
          className="absolute inset-x-0 bottom-0 flex flex-col items-center"
          style={{ padding: '0 6cqw 5.5cqw', gap: '2.6cqw' }}
        >
          <span
            className="font-extrabold italic"
            style={{
              fontSize: '12.5cqw',
              lineHeight: 1,
              letterSpacing: '0.01em',
              background: 'linear-gradient(180deg,#fbcfe8,#ec4899)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              filter: 'drop-shadow(0 2px 12px rgba(236,72,153,0.45))',
              ...pop(1.04),
            }}
          >
            UNLOCK&nbsp;MORE
          </span>
          <span className="text-gray-200" style={{ fontSize: '4.6cqw' }}>
            Premium chat, images and calls
          </span>

          <span
            className="flex flex-col items-center"
            style={{ gap: '0.8cqw', ...pop(1.05) }}
          >
            <span
              className="font-semibold text-pink-300/90 uppercase"
              style={{ fontSize: '3.6cqw', letterSpacing: '0.25em' }}
            >
              As low as
            </span>
            <span className="flex items-baseline" style={{ gap: '2.4cqw' }}>
              <span
                className="text-gray-400 line-through"
                style={{ fontSize: '6cqw' }}
              >
                {symbol}12.99
              </span>
              <span
                className="font-extrabold"
                style={{
                  fontSize: '15cqw',
                  lineHeight: 1,
                  background: 'linear-gradient(180deg,#f9a8d4,#ec4899)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  filter: 'drop-shadow(0 0 18px rgba(236,72,153,0.55))',
                }}
              >
                {symbol}5.99
              </span>
              <span className="text-gray-300" style={{ fontSize: '5cqw' }}>
                /mo
              </span>
            </span>
          </span>

          {/* Trial divider — mock: — 3-day free trial — */}
          <span
            className="flex w-full items-center text-pink-200/90 italic"
            style={{ gap: '2.4cqw', fontSize: '4cqw' }}
          >
            <span
              aria-hidden
              style={{
                flex: 1,
                height: 1,
                background: 'rgba(244,114,182,0.45)',
              }}
            />
            3-day free trial
            <span
              aria-hidden
              style={{
                flex: 1,
                height: 1,
                background: 'rgba(244,114,182,0.45)',
              }}
            />
          </span>

          {/* CTA */}
          <span
            className="relative inline-flex w-full items-center justify-center overflow-hidden font-bold text-white"
            style={{
              padding: '3.6cqw 4cqw',
              fontSize: '5.8cqw',
              borderRadius: '14px',
              background: 'linear-gradient(90deg,#ec4899,#d946ef)',
              boxShadow: '0 0 22px rgba(236,72,153,0.55)',
              ...pop(1.05),
            }}
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%)',
                transition: 'transform 0.7s ease-out',
                transform: hovered ? 'translateX(100%)' : 'translateX(-100%)',
              }}
            />
            <span
              className="relative z-10 inline-flex items-center"
              style={{ gap: '2cqw' }}
            >
              {loading ? (
                <Loader2
                  className="animate-spin"
                  style={{ width: '5.8cqw', height: '5.8cqw' }}
                />
              ) : (
                <>
                  START FREE TRIAL
                  <ChevronRight style={{ width: '5.8cqw', height: '5.8cqw' }} />
                </>
              )}
            </span>
          </span>

          {/* Trust footer */}
          <span
            className="flex items-center justify-center text-gray-400"
            style={{ gap: '3cqw', fontSize: '3.6cqw' }}
          >
            <span
              className="inline-flex items-center"
              style={{ gap: '1.4cqw' }}
            >
              <ShieldCheck style={{ width: '3.8cqw', height: '3.8cqw' }} />
              Secure Payments
            </span>
            <span className="text-gray-600">|</span>
            <span
              className="inline-flex items-center"
              style={{ gap: '1.4cqw' }}
            >
              <RefreshCcw style={{ width: '3.8cqw', height: '3.8cqw' }} />
              Cancel Anytime
            </span>
          </span>
        </span>
      </button>

      <CheckoutOverlay
        checkoutToken={checkoutToken}
        hideBack
        onBack={() => setCheckoutToken(null)}
        onComplete={() => setCheckoutToken(null)}
      />
    </>
  );
}
